import React from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import {  ActionBar } from "@project-stories/blocks/ActionBar/ActionBar";


const OrangeDot = ({ size = 8, color = "#F2610D", ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle cx="4.5" cy="4" r="4" fill={color} />
    </svg>
);

const LargeDot = ({ size = 6, color = "#000000", ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
    {...props}
  >
    <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} />
  </svg>
);

function stripHtml(html: string): string {
    if (!html) return "";
    // Remove HTML tags
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}


function getReadTime(html: string): string {
    const text = stripHtml(html);
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const mins = Math.floor(wordCount / 200) + 1;
    return `${mins} min read`;
}


const BlogPostLayout = ({ settings }) => {
    const { globals: { mobile_breakpoint, shop_url  }, blocks, article: { tags, content,  metafields, title, image, author , created_at, url } } = settings;
    const isMobile = useMobileBreakpoint(mobile_breakpoint);
    const readTime = metafields?.read_time || (content ? getReadTime(content) : "1 min read");
    const articleCategories = tags  && tags.slice(0, 2);
    const articleUrl = `${shop_url}${url}`;

  const renderBlock = (block, index) => {
    switch (block.type) {
      case "title":
        return (
          <div
            key={`title_${index}_column_${block.settings.column}`}
            className={`
              flex h-full items-center
              
              justify-${block.settings.content_alignment_mobile}
              lg:justify-${block.settings.content_alignment_desktop}
              
              pt-[${block.settings.padding_top_mobile}px]
              lg:pt-[${block.settings.padding_top_desktop}px]
              
              pb-[${block.settings.padding_bottom_mobile}px] 
              lg:pb-[${block.settings.padding_bottom_desktop}px]
              
              px-[${block.settings.padding_left_right_mobile}px]
              lg:px-[${block.settings.padding_left_right_desktop}px]
            `}
          >
            <div className="flex flex-col gap-2">
              <h1 className={`hidden lg:block ${block.settings.heading_type}`}>
                {block.settings.title}
              </h1>

              <h1 className={`lg:hidden block ${block.settings.heading_type_mobile}`}>
                {block.settings.title}
              </h1>

            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`blog-post-header overflow-hidden`}>
      {isMobile ? (
        <>
          <div className={`blog-post-header__content-container w-full`}> 

            <div> 
                {blocks.map(renderBlock)}
            </div>

            {/* Render image only if it exists */}
            {image && (
              <div className={`blog-post-header__image-container w-full`}> 
                <img
                  src={image}
                  alt=""
                />
              </div>
            )}

            {/* Render author/created_at row only if at least one exists */}
            {(author || created_at) && (
              <div className={`items-center gap-2 flex flex-row justify-start`}> 
                {author && (
                  <span className={`b4`}>
                    By <span className={`b4--bold`}> {author} </span>
                  </span>
                )}
                {author && created_at && (
                  <span className="flex items-center justify-center">
                    <LargeDot />
                  </span>
                )}
                {created_at && (
                  <span className={`b4`}>
                    {created_at}
                  </span>
                )}
              </div>
            )}
          </div>

        </>
      ) : (
        <>
          <div className={`blog-post-header__content-container w-1/2`}> 
            <div className="blog-post-header__metadata">
                {readTime && (
                <span className="blog-post-header__category b3--bold">{readTime}</span>
                )}
                {articleCategories && articleCategories.map((tag, idx) => (
                <React.Fragment key={`${tag}-${idx}`}>
                    <OrangeDot />
                    <span className="blog-post-header__category b3--bold">{tag}</span>
                </React.Fragment>
                ))}
            </div>

            <div> 
                {blocks.map(renderBlock)}
            </div>

            {/* Render author/created_at row only if at least one exists */}
            {(author || created_at) && (
              <div className={`items-center gap-2 flex flex-row justify-start`}> 
                {author && (
                  <span className={`b4`}>
                    By <span className={`b4--bold`}> {author} </span>
                  </span>
                )}
                {author && created_at && (
                  <span className="flex items-center justify-center">
                    <LargeDot />
                  </span>
                )}
                {created_at && (
                  <span className={`b4`}>
                    {created_at}
                  </span>
                )}
              </div>
            )}

            <ActionBar
                url={articleUrl}
            />
          </div>
          {/* Render image only if it exists */}
          {image && (
            <div className={`blog-post-header__image-container w-1/2`}> 
              <img
                src={image}
                alt=""
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlogPostLayout;
